import { Intent } from '@blueprintjs/core';
import * as React from 'react';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { withConfirmation } from 'src/components/withConfirmation';
import { useClearDiagram } from 'src/containers/ProjectDiagram/hooks/useClearDiagram';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  ModelConceptClass,
  ModelConceptRelationship,
} from 'src/containers/ProjectDiagram/types/data';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import { addDeletedData } from 'src/containers/ProjectDiagram/utils/addDeletedData';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import {
  isModelConceptClass,
  isModelConceptRelationship,
} from 'src/containers/ProjectDiagram/utils/data';
import { removeModelConceptAttribute } from 'src/containers/ProjectDiagram/utils/modelConceptAttribute/removeModelConceptAttribute';
import { removeModelConceptClass } from 'src/containers/ProjectDiagram/utils/modelConceptClass/removeModelConceptClass';
import { removeModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/removeModelConceptRelationship';
import {
  getProjectModelIdsForIri,
  removeProjectModelId,
} from 'src/containers/ProjectDiagram/utils/projectModelIds';
import { removeRule } from 'src/containers/ProjectDiagram/utils/rules/removeRule';
import { updateRule } from 'src/containers/ProjectDiagram/utils/rules/updateRule';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import { projectSelector } from 'src/selectors/project';
import { projectModelSelector } from 'src/selectors/projectModel';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const useRemoveProjectModel = (modelId: string) => {
  const dispatch = useReduxDispatch();
  const diagram = useDiagram();
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const projectModel = useReduxSelector(
    projectModelSelector(projectId, modelId)
  );
  const clearDiagram = useClearDiagram();

  return React.useCallback(() => {
    if (!diagram || !projectId || !project || !projectModel) {
      return;
    }

    const shouldDelete = (iri: string) => {
      // only delete things when they are in this model and not any others
      const modelIds = getProjectModelIdsForIri(iri, diagram.model);
      return modelIds.length === 1 && modelIds.includes(modelId);
    };

    const classes: ModelConceptClass[] = [];
    const relationships: ModelConceptRelationship[] = [];
    const attributeIris: string[] = Object.keys(
      diagram.model.modelData.attributesByIri
    ).filter(shouldDelete);
    const ruleIds: string[] = [];

    diagram.model.nodeDataArray.forEach((data) => {
      if (shouldDelete(data.value) && isModelConceptClass(data)) {
        classes.push(data);
      }
    });

    diagram.model.linkDataArray.forEach((data) => {
      if (shouldDelete(data.value) && isModelConceptRelationship(data)) {
        relationships.push(data);
      }
    });

    Object.values(diagram.model.modelData.rulesById).forEach((rule) => {
      if (rule.models.includes(modelId)) {
        ruleIds.push(rule.id);
      }
    });

    // close all drawers before opening popup, so that we aren't
    // left with a drawer open on something deleted
    clearDiagram();

    withConfirmation({
      isCloseButtonShown: false,
      message: copy.components.projectDiagram.modelDeletionWarning.message(
        projectModel.name,
        classes.length,
        relationships.length,
        attributeIris.length,
        ruleIds.length
      ),
      noButtonOutlined: true,
      noPrompt: copy.components.projectDiagram.modelDeletionWarning.cancel,
      title: null,
      yesButtonIntent: Intent.PRIMARY,
      yesPrompt: copy.components.projectDiagram.modelDeletionWarning.proceed,
      onConfirmationSuccess: () => {
        const deletedModelIds = [
          ...(diagram.model.modelData.deletedModelIds || []),
          modelId,
        ];

        commitDiagramModel(diagram, (model) => {
          addDeletedData([...relationships, ...classes])(model);

          attributeIris.forEach((value) => {
            removeModelConceptAttribute({ value })(model);
          });
          relationships.forEach((data) => {
            removeModelConceptRelationship(data)(model);
          });
          classes.forEach((data) => {
            removeModelConceptClass(data)(model);
          });
          ruleIds.forEach((ruleId) => {
            const rule = { ...model.modelData.rulesById[ruleId] };
            const models = rule.models.filter((id) => id !== modelId);
            if (models.length) {
              updateRule({ ...rule, models })(model);
            } else {
              removeRule(ruleId)(model);
            }
          });
          removeProjectModelId(modelId)(model);

          if (model.modelData.hiddenModelIds.includes(modelId)) {
            const hiddenModels = model.modelData.hiddenModelIds.filter(
              (id) => id !== modelId
            );
            setDataProperty<ModelData>(model)(
              model.modelData,
              'hiddenModelIds',
              hiddenModels
            );
          }

          setDataProperty<ModelData>(model)(
            model.modelData,
            'deletedModelIds',
            deletedModelIds
          );
        });

        // update targetModelId to be another model if we are deleting the target
        if (project.targetModelId === modelId) {
          const otherModelId = Object.keys(project.modelsById).find(
            (id) => !deletedModelIds.includes(id)
          );
          if (otherModelId) {
            dispatch(
              projectsActionCreators.updatedProjectTargetModelId(
                projectId,
                otherModelId
              )
            );
          }
        }
      },
    });
  }, [
    clearDiagram,
    diagram,
    dispatch,
    modelId,
    project,
    projectId,
    projectModel,
  ]);
};
